import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ArrowLeft } from "lucide-react";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import GoogleSignInButton from "@/components/GoogleSignInButton";
import TwoFactorStep from "@/components/TwoFactorStep";

const routeForUser = (u) =>
  u?.role === "rep" ? (u?.parent_user_id ? "/rep" : "/academy")
  : u?.role === "customer" ? "/customer/dashboard" : "/app";

export default function Login() {
  const { login, verifyTwoFactor } = useAuth();
  const nav = useNavigate();
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [twofa, setTwofa] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    const res = await login(email, password);
    setBusy(false);
    if (res.twofa) { setTwofa(res.twofa); return; }
    if (res.ok) {
      toast.success(t("auth.welcome_back"));
      nav(routeForUser(res.user));
    }
    else {
      const msg = res.error || t("errors.generic");
      setError(msg);
      toast.error(msg);
    }
  };

  if (twofa) {
    return (
      <AuthShell>
        <TwoFactorStep
          challenge={twofa}
          onVerified={verifyTwoFactor}
          onBack={() => setTwofa(null)}
          onDone={(u) => { toast.success(t("auth.welcome_back")); nav(routeForUser(u)); }}
        />
      </AuthShell>
    );
  }

  return (
    <AuthShell>
      <h1 className="font-heading text-3xl font-bold">{t("auth.welcome_back")}</h1>
      <p className="text-slate-400 text-sm mt-1">{t("auth.sign_in_to_continue")}</p>
      <form onSubmit={submit} className="mt-6 space-y-4">
        {error && (
          <div className="bg-red-500/10 border border-red-500/40 text-red-300 text-sm rounded-md px-4 py-3" data-testid="login-error">
            {error}
          </div>
        )}
        <Field label={t("auth.email")} value={email} onChange={setEmail} type="email" testId="login-email" />
        <Field label={t("auth.password")} value={password} onChange={setPassword} type="password" testId="login-password" />
        <div className="text-right -mt-2">
          <Link to="/forgot-password" className="text-xs text-slate-400 hover:text-[var(--ld-royal-hover)] hover:underline"
            data-testid="login-forgot-link">{t("auth.forgot_password")}</Link>
        </div>
        <button className="ld-btn ld-btn-primary w-full justify-center" data-testid="login-submit" disabled={busy}>
          {busy ? t("auth.signing_in") : t("common.log_in")}
        </button>
      </form>
      <div className="mt-5 flex items-center gap-3" aria-hidden="true">
        <div className="flex-1 h-px bg-white/10" />
        <span className="text-[11px] uppercase tracking-widest text-slate-500">{t("auth.or")}</span>
        <div className="flex-1 h-px bg-white/10" />
      </div>
      <div className="mt-5">
        <GoogleSignInButton testId="login-google-btn" />
      </div>
      <p className="mt-6 text-sm text-slate-400">
        {t("auth.no_account")} <Link to="/register" className="text-[var(--ld-royal-hover)] hover:underline" data-testid="login-goto-register">{t("nav.start_trial")}</Link>
      </p>
    </AuthShell>
  );
}

function Field({ label, value, onChange, type = "text", testId }) {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="text-xs uppercase tracking-widest text-slate-500">{label}</span>
      <input
        type={type} value={value} onChange={(e)=>onChange(e.target.value)} required
        className="bg-[var(--ld-surface-2)] border border-white/10 rounded-md px-4 py-2.5 text-sm focus:outline-none focus:border-[var(--ld-royal)] focus:ring-2 focus:ring-[var(--ld-royal-glow)]"
        data-testid={testId}
      />
    </label>
  );
}

export function AuthShell({ children }) {
  const { t } = useTranslation();
  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[var(--ld-bg)] text-white">
      <div className="p-8 flex flex-col justify-center max-w-md mx-auto w-full">
        <div className="flex items-center justify-between mb-10">
          <Link to="/" className="flex items-center gap-2" data-testid="auth-brand">
            <div className="w-8 h-8 rounded-md bg-[var(--ld-royal)] flex items-center justify-center font-heading font-bold">L</div>
            <span className="font-heading">Legacy Detailing <span className="text-slate-500">IO</span></span>
          </Link>
          <div className="flex items-center gap-3">
            <LanguageSwitcher variant="nav" />
            <Link
              to="/"
              className="inline-flex items-center gap-1.5 text-xs text-slate-400 hover:text-white transition-colors group"
              data-testid="login-back-to-landing"
            >
              <ArrowLeft size={14} className="group-hover:-translate-x-0.5 transition-transform" strokeWidth={2} />
              {t("common.back_to_home")}
            </Link>
          </div>
        </div>
        {children}
      </div>
      <div className="hidden lg:block relative overflow-hidden">
        <img src="https://images.unsplash.com/photo-1626428091984-48f9ffbf927c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMHNpbHZlciUyMHJveWFsJTIwYmx1ZSUyMDNkJTIwdGV4dHVyZXxlbnwwfHx8fDE3ODM0NTUzMTJ8MA&ixlib=rb-4.1.0&q=85" className="w-full h-full object-cover" alt="" />
        <div className="absolute inset-0 bg-gradient-to-tr from-black/70 to-transparent" />
      </div>
    </div>
  );
}
